import React, { useCallback, useMemo } from "react";
import ModalWrapper from "..";
import { Button } from "../../button";
import toast from "react-hot-toast";
import useAuthStore from "../../../store/Auth";
import { useTranslation } from "react-i18next";
import { useCheckOutVisitor } from "../../../hooks/visitor/mutation";
import {
  CHECK_IN_OUT_TYPE_KEYS,
  CHECK_IN_OUT_TYPES,
} from "../../../constants/common";
import { AlertCircle } from "lucide-react";
import TextInput from "../../inputs/TextInput";
import { Controller } from "react-hook-form";
import useFormWithSchema from "../../../hooks/useFormWithSchema";
import * as Yup from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import { APP_ROUTES } from "../../../routes";

interface CheckOutVisitorModalProps {
  appointmentId: number;
  visitEndDate: string;
  isOpen: boolean;
  onOpenChange: () => void;
  gate?: string;
}

const CheckOutVisitorModalSchema = Yup.object({
  reason: Yup.string(),
});

interface FormData {
  reason?: string;
}

const CheckOutVisitorModal = ({
  appointmentId,
  visitEndDate,
  isOpen,
  onOpenChange,
  gate,
}: CheckOutVisitorModalProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = new URLSearchParams(location.search);
  const type = searchParams.get("type");

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useFormWithSchema(CheckOutVisitorModalSchema);

  const { mutateAsync: CheckOutVisitor, isPending: isCheckOutVisitorLoading } =
    useCheckOutVisitor();

  const { t } = useTranslation();

  const isLateCheckOut = useMemo(
    () => new Date(visitEndDate) < new Date(),
    [visitEndDate]
  );

  const user = useAuthStore((state) => state.user);

  const onSubmit = useCallback(
    async (formData: FormData) => {
      try {
        if (user?.user_id) {
          const body = {
            encryptedId: appointmentId.toString(),
            ...(isLateCheckOut &&
              formData.reason && { S_Reason: formData.reason }),
            N_CheckedOutType:
              type === CHECK_IN_OUT_TYPE_KEYS.VEHICLE
                ? CHECK_IN_OUT_TYPES.VEHICLE
                : CHECK_IN_OUT_TYPES.VMS,
            ...(type === CHECK_IN_OUT_TYPE_KEYS.VEHICLE && { N_GateID: gate }),
          };

          await CheckOutVisitor(body);
          toast.success(t("visitorCheckedOutSuccessfully"));

          if (location.pathname === APP_ROUTES.CHECK_OUT) {
            setTimeout(() => {
              navigate(APP_ROUTES.HOME);
            }, 700);
          }
        }
      } catch (error) {
        toast.error(t("somethingWentWrong"));
      } finally {
        onOpenChange();
      }
    },
    [
      user?.user_id,
      appointmentId,
      isLateCheckOut,
      type,
      gate,
      CheckOutVisitor,
      t,
      location.pathname,
      navigate,
      onOpenChange,
    ]
  );

  return (
    <ModalWrapper isOpen={isOpen} onOpenChange={onOpenChange}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-8 justify-center items-center"
      >
        {isLateCheckOut && (
          <div className="flex items-center gap-2 max-w-max rounded-md px-2 -mb-4">
            <AlertCircle className="w-6 text-red-500" />
            <p className="text-lg uppercase text-red-500 font-semibold">
              {t("visitTimeHasEnded")}
            </p>
          </div>
        )}

        <p className="text-lg md:text-2xl font-medium">
          {t("areYouSureYouWantToCheckoutThisVisitor")}
        </p>

        {isLateCheckOut && (
          <div className="w-full">
            <Controller
              name="reason"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <TextInput
                  label={t("reason")}
                  onChange={onChange}
                  error={errors?.[name]?.message}
                  value={value}
                />
              )}
            />
          </div>
        )}

        <div className="flex gap-1 items-center">
          <Button
            isLoading={isCheckOutVisitorLoading}
            className=" bg-red-600 hover:bg-red-700"
          >
            {t("checkOut")}
          </Button>

          <Button onClick={onOpenChange}>{t("close")}</Button>
        </div>
      </form>
    </ModalWrapper>
  );
};

export default React.memo(CheckOutVisitorModal);
